import React, { ComponentProps } from 'react'
import useTranslation from 'next-translate/useTranslation'
import { Text } from 'components/Text'
import { Stack } from 'components/Stack'
import { Button } from 'components/Button'
import { Product } from 'models/Product'
import { ProductCardRoot, Data } from './ProductCard.styles'
import { formatCurrency } from 'utils/currency'
import Image from 'next/image'
import { Link } from 'components/Link/Link'

type ProductCardOwnProps = ComponentProps<typeof ProductCardRoot>
export type ProductCardProps = ProductCardOwnProps & {
  product: Product
  onAdd: (productId: string, quantity: number, productName: string) => void
}

export const ProductCard = ({ product, onAdd }: ProductCardProps) => {
  const { t } = useTranslation()

  return (
    <ProductCardRoot direction="column">
      <Link href={`/products/${product.slug}`}>
        <a aria-label={t('e-commerce:link-aria', { product: product.name })}>
          <Image src={product.image} width={250} height={250} objectFit="contain" alt="" />
        </a>
      </Link>
      <Data direction="column" justifyContent="space-between">
        <Stack direction="column" alignItems="flex-start">
          <Link
            href={`/products/${product.slug}`}
            passHref
            aria-label={t('e-commerce:link-aria', { product: product.name })}
          >
            <Text as="a" size="headline" weight="bold" color="primary-800">
              {product.name}
            </Text>
          </Link>
          <Stack direction="column">
            <Text color="primary-500">
              {product.price.discount > 0 ? (
                <Stack>
                  <Text weight="medium" lineThrough>
                    &nbsp;{formatCurrency(product.price.subtotal)}&nbsp;
                  </Text>
                  <Text>{formatCurrency(product.price.total)}</Text>
                </Stack>
              ) : (
                <>
                  <Text weight="medium">{formatCurrency(product.price.total)}</Text>
                  <Text>{t('common:per-unit')}</Text>
                </>
              )}
            </Text>
            {product.unitWeight && (
              <Text color="primary-800">{`${product.weight / product.unitWeight} x ${
                product.unitWeight
              }oz`}</Text>
            )}
          </Stack>
        </Stack>
        <Button
          variant="tertiary"
          size="small"
          onClick={() => onAdd(product.productId, 1, product.name)}
        >
          {t('e-commerce:add-to-cart')}
        </Button>
      </Data>
    </ProductCardRoot>
  )
}

ProductCard.displayName = 'ProductCard'
